'use client';

import * as React from 'react';

const PROTECTED_HOSTNAME = process.env.REACT_APP_ANALYTICS_HOSTNAME ?? 'analytics.mynextclassic.net';
const API_BASE = process.env.REACT_APP_ENDPOINT_BASE ?? 'https://mynextclassic-backend.net';
const TOKEN_KEY = process.env.REACT_APP_AUTH_TOKEN_KEY ?? 'mnc_analytics_token';

const isProtectedDomain = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.location.hostname === PROTECTED_HOSTNAME;
};

const getStoredToken = (): string | null => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};

const storeToken = (token: string) => {
  try { localStorage.setItem(TOKEN_KEY, token); } catch {}
};

const clearToken = () => {
  try { localStorage.removeItem(TOKEN_KEY); } catch {}
};

interface LoginGateProps {
  children: React.ReactNode;
}

type AuthState = 'checking' | 'unauthenticated' | 'authenticated';

export const LoginGate: React.FC<LoginGateProps> = ({ children }) => {
  if (!isProtectedDomain()) return <>{children}</>;
  return <ProtectedGate>{children}</ProtectedGate>;
};

const ProtectedGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = React.useState<AuthState>('checking');
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [shaking, setShaking] = React.useState(false);

  React.useEffect(() => {
    const token = getStoredToken();
    if (!token) { setAuthState('unauthenticated'); return; }
    fetch(`${API_BASE}/auth/verify`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        if (res.ok) { setAuthState('authenticated'); }
        else { clearToken(); setAuthState('unauthenticated'); }
      })
      .catch(() => setAuthState('unauthenticated'));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim().toLowerCase(), password }),
      });
      if (res.ok) {
        const { token } = await res.json();
        storeToken(token);
        setAuthState('authenticated');
      } else {
        setError('Invalid credentials.');
        setShaking(true);
        setTimeout(() => setShaking(false), 500);
        setPassword('');
      }
    } catch {
      setError('Could not reach server. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (authState === 'checking') {
    return (
      <div className="min-h-screen bg-mp-bg flex items-center justify-center">
        <p className="font-mono text-mp-muted text-xs tracking-widest animate-pulse">
          &gt; VERIFYING SESSION...
        </p>
      </div>
    );
  }

  if (authState === 'authenticated') return <>{children}</>;

  return (
    <div className="min-h-screen bg-mp-bg flex flex-col items-center justify-center px-4">
      <div className="mb-10 text-center flex flex-col items-center gap-2">
        <p className="font-mono text-[10px] tracking-[0.3em] text-mp-muted uppercase">
          MyNextClassic
        </p>
        <h1 className="font-display text-2xl font-semibold text-mp-text uppercase tracking-widest">
          Analytics
        </h1>
        <div className="h-px w-10 bg-primary" />
      </div>

      <div
        className="w-full max-w-sm bg-mp-surface border border-mp-border p-8 flex flex-col gap-6"
        style={shaking ? { animation: 'shake 0.4s ease-in-out' } : {}}
      >
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] text-mp-muted uppercase tracking-widest">
            &gt; Restricted Access
          </span>
          <span className="material-symbols-outlined text-mp-muted text-base">lock</span>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="font-display text-[10px] uppercase tracking-widest text-mp-muted">
              Username
            </label>
            <input
              type="text"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={e => { setUsername(e.target.value); setError(''); }}
              disabled={submitting}
              className="bg-mp-bg border border-mp-border text-mp-text font-mono text-sm px-3 py-2.5 focus:outline-none focus:border-primary transition-colors disabled:opacity-50 placeholder:text-mp-muted/40"
              placeholder="username"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="font-display text-[10px] uppercase tracking-widest text-mp-muted">
              Password
            </label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={e => { setPassword(e.target.value); setError(''); }}
              disabled={submitting}
              className="bg-mp-bg border border-mp-border text-mp-text font-mono text-sm px-3 py-2.5 focus:outline-none focus:border-primary transition-colors disabled:opacity-50 placeholder:text-mp-muted/40"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 border border-primary/30 bg-primary/10 px-3 py-2">
              <span className="material-symbols-outlined text-primary text-[14px]">error</span>
              <p className="font-mono text-[11px] text-primary tracking-wide">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="mt-1 h-11 bg-primary hover:bg-primary-hover disabled:opacity-50 text-mp-text font-display text-xs uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <span className="material-symbols-outlined text-base animate-spin">progress_activity</span>
                Verifying...
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-base">login</span>
                Enter
              </>
            )}
          </button>
        </form>
      </div>

      <p className="mt-8 font-mono text-[10px] text-mp-muted/30 tracking-widest uppercase">
        Partner Preview — Confidential
      </p>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%       { transform: translateX(-6px); }
          40%       { transform: translateX(6px); }
          60%       { transform: translateX(-4px); }
          80%       { transform: translateX(4px); }
        }
      `}</style>
    </div>
  );
};

export default LoginGate;

// ─── Add this export at the bottom of LoginGate.tsx ──────────────────────────

export const LogoutButton: React.FC = () => {
  if (!isProtectedDomain()) return null;

  const handleLogout = () => {
    const token = getStoredToken();
    if (token) {
      fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    clearToken();
    window.location.reload();
  };

  return (
    <button
      onClick={handleLogout}
      className="flex items-center gap-1.5 font-mono text-[10px] text-mp-muted hover:text-primary uppercase tracking-widest transition-colors"
    >
      <span className="material-symbols-outlined text-[14px]">logout</span>
      Sign Out
    </button>
  );
};
